import { dirname, join, parse, resolve } from 'node:path';
import { mkdir, readFile, writeFile } from 'node:fs/promises';
import { constants } from 'node:fs';
import { access } from 'node:fs/promises';

export interface CliStackChoice {
  provider: string;
  selectedAt: string;
}

export interface CliStackChoicesFile {
  version: 1;
  choices: Record<string, CliStackChoice>;
}

export function getStackChoicesPath(cwd: string = process.cwd()): string {
  return join(getProjectArtifactsDir(cwd), 'stack.json');
}

export function getProjectArtifactsDir(cwd: string = process.cwd()): string {
  return join(cwd, '.viberaven');
}

const LAST_SCAN_FILE = 'last-scan.json';

/**
 * Walk up from `startDir` for `.viberaven/last-scan.json` (nearest ancestor wins).
 */
export async function findArtifactsWorkspace(startDir: string = process.cwd()): Promise<string | undefined> {
  let dir = resolve(startDir);
  const fsRoot = parse(dir).root;

  while (true) {
    try {
      await access(join(dir, '.viberaven', LAST_SCAN_FILE), constants.F_OK);
      return dir;
    } catch {
      // keep walking up
    }
    const parent = dirname(dir);
    if (parent === dir || dir === fsRoot) {
      return undefined;
    }
    dir = parent;
  }
}

/**
 * Best folder for scan/read artifacts: nearest `.viberaven` scan, else git root, else `startDir`.
 */
export async function resolveWorkspaceRoot(startDir: string = process.cwd()): Promise<string> {
  const withArtifacts = await findArtifactsWorkspace(startDir);
  if (withArtifacts) {
    return withArtifacts;
  }

  let dir = resolve(startDir);
  const fsRoot = parse(dir).root;

  while (true) {
    try {
      await access(join(dir, '.git'), constants.F_OK);
      return dir;
    } catch {
      // keep walking up
    }
    const parent = dirname(dir);
    if (parent === dir || dir === fsRoot) {
      return resolve(startDir);
    }
    dir = parent;
  }
}

export async function loadStackChoicesFile(cwd: string): Promise<CliStackChoicesFile> {
  try {
    const raw = await readFile(getStackChoicesPath(cwd), 'utf-8');
    const parsed = JSON.parse(raw) as Partial<CliStackChoicesFile>;
    if (parsed && parsed.version === 1 && parsed.choices && typeof parsed.choices === 'object') {
      return { version: 1, choices: parsed.choices };
    }
  } catch {
    // optional file
  }
  return { version: 1, choices: {} };
}

export async function saveStackChoicesFile(cwd: string, file: CliStackChoicesFile): Promise<void> {
  await mkdir(getProjectArtifactsDir(cwd), { recursive: true });
  await writeFile(getStackChoicesPath(cwd), `${JSON.stringify(file, null, 2)}\n`, 'utf-8');
}
